import { redirect } from "@remix-run/cloudflare";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import {
  Link,
  useLoaderData,
  useNavigation,
  useSearchParams,
} from "@remix-run/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseLayout } from "~/components/BaseLayout";
import { Button } from "~/components/ui/Button";
import { fallbackLng, supportedLngs } from "~/i18n";
import { ApiClient, OmniProduct } from "~/lib/api";
import { mapShopProductToGA4Item, trackGA4ViewItemList } from "~/lib/gtm-datalayer";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref } from "~/lib/utils";
import { WooCommerceClient } from "~/lib/woocommerce";
import { useShopCartStore } from "~/stores/shopCart";
import { ShopCategory, ShopFilters, ShopProduct } from "~/types/shop";

export const sitemap = createSitemapFunction("shop");

interface LoaderData {
  products: ShopProduct[];
  categories: ShopCategory[];
  total: number;
  totalPages: number;
  currentFilters: ShopFilters;
  locale: string;
}

export async function loader({ request, context, params }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const env: any = context?.cloudflare?.env;
  const rawLocale = params?.locale || url.pathname.split("/")[1] || fallbackLng;
  const locale = supportedLngs.includes(rawLocale) ? rawLocale : fallbackLng;

  const shopWip = (env?.SHOP_WIP ?? "").toString().toLowerCase() === "true";
  if (shopWip) {
    return redirect(getMenuHref(locale, "/shop/wip"));
  }
  const shopDisabled =
    (env?.SHOP_DISABLED ?? "").toString().toLowerCase() === "true";
  if (shopDisabled) {
    return redirect(getMenuHref(locale, "/coming-soon"));
  }
  const searchParams = url.searchParams;

  const filters: ShopFilters = {
    category: searchParams.get("category") || undefined,
    category_id: searchParams.get("category_id")
      ? parseInt(searchParams.get("category_id")!)
      : undefined,
    category_slug:
      (searchParams.get("category_slug") as
        | "single-entry"
        | "couple-entry"
        | "all") || "all",
    per_page: 20,
    page: searchParams.get("page") ? parseInt(searchParams.get("page")!) : 1,
  };

  async function getProducts(env: any) {
    const productsFromCache = await env.KV.get("shop-products", "json");
    return productsFromCache || [];
  }

  try {
    let productsFromCache = await getProducts(env);

    if (!productsFromCache || productsFromCache.length === 0) {
      try {
        const apiClient = new ApiClient(env);
        const response = await apiClient.getProducts();
        const productsArray = response.data.data || [];

        const activeProducts = productsArray.filter(
          (product: any) => product.Suspended === "No",
        );

        if (activeProducts.length > 0) {
          await env.KV.put("shop-products", JSON.stringify(activeProducts), {
            expirationTtl: 3600 * 1, // 1 hour
          });
          productsFromCache = activeProducts;
        }
      } catch (error) {
        console.error("Error fetching products from API:", error);
      }
    }

    const activeProductCodes = new Set(
      (productsFromCache || []).map((product: any) => product.Product),
    );

    const productsCacheMap = new Map<string, OmniProduct>(
      (productsFromCache || []).map((product: OmniProduct) => [
        product.Product,
        product,
      ]),
    );

    const wooCommerceClient = new WooCommerceClient({
      WP_API_HOST: env?.WP_API_HOST || "",
      WC_CONSUMER_KEY: env?.WC_CONSUMER_KEY || "",
      WC_CONSUMER_SECRET: env?.WC_CONSUMER_SECRET || "",
      I18N_DISABLED: env?.I18N_DISABLED,
    });

    const productsResponse = await wooCommerceClient.getProducts(filters, locale);
    let wooProducts = productsResponse.data;

    wooProducts = wooProducts
      .filter((product: any) => {
        const productCode = product.sku;

        if (!productCode) {
          return false;
        }

        if (!activeProductCodes.has(productCode)) {
          return false;
        }

        const cachedProduct = productsCacheMap.get(productCode);
        if (!cachedProduct) {
          return false;
        }

        const promotionalPrice = cachedProduct.PromotionalPrice ?? 0;
        const standardPrice = cachedProduct.StandardPrice ?? 0;

        if (promotionalPrice > 0) {
          return true;
        }

        if (standardPrice > 0) {
          return true;
        }

        return false;
      })
      .map((product: any) => {
        const productCode = product.sku;
        const cachedProduct = productsCacheMap.get(productCode);

        if (cachedProduct) {
          const promotionalPrice = cachedProduct.PromotionalPrice ?? 0;
          const standardPrice = cachedProduct.StandardPrice ?? 0;

          let price: number;
          if (promotionalPrice > 0) {
            price = promotionalPrice;
          } else if (standardPrice > 0) {
            price = standardPrice;
          } else {
            return null;
          }

          return {
            ...product,
            price: price.toString(),
            regular_price:
              standardPrice > 0
                ? standardPrice.toString()
                : product.regular_price,
            sale_price:
              promotionalPrice > 0
                ? promotionalPrice.toString()
                : product.sale_price,
            external_id: cachedProduct.Code,
            meta_data: [
              ...(product.meta_data || []),
              {
                id: Date.now(),
                key: "external_id",
                value: cachedProduct.Code,
              },
            ],
          };
        }

        return null;
      })
      .filter((product: any) => product !== null);

    const categoriesResponse = await wooCommerceClient.getProductCategories(locale);
    const categoriesData = (categoriesResponse.data || []).filter(
      (cat: any) => cat.count > 0
    );

    return json({
      products: wooProducts,
      categories: categoriesData,
      total: wooProducts.length,
      totalPages: productsResponse.headers["x-wp-totalpages"]
        ? parseInt(productsResponse.headers["x-wp-totalpages"])
        : 1,
      currentFilters: filters,
      locale,
    });
  } catch (error) {
    console.error("Error loading products:", error);
    return json({
      products: [],
      categories: [],
      total: 0,
      totalPages: 1,
      currentFilters: filters,
      locale,
    });
  }
}

export default function ShopPage() {
  const { t } = useTranslation();
  const { products, categories, locale } = useLoaderData<LoaderData>();
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>("all");
  const isLoading = navigation.state === "loading";

  const { addToCart, getTotalItems } = useShopCartStore();
  const [addedProducts, setAddedProducts] = useState<Set<number>>(new Set());

  useEffect(() => {
    const categoryId = searchParams.get("category_id");
    if (categoryId) {
      setActiveTab(categoryId);
    } else {
      setActiveTab("all");
    }
  }, [searchParams]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(searchParams);

    if (tab === "all") {
      newSearchParams.delete("category_id");
      newSearchParams.delete("category_slug");
    } else {
      const category = categories.find((cat) => cat.id.toString() === tab);
      if (category) {
        newSearchParams.set("category_id", category.id.toString());
        newSearchParams.delete("category_slug");
      }
    }

    setSearchParams(newSearchParams);
  };

  const handleAddToCart = (product: ShopProduct) => {
    if (addedProducts.has(product.id)) return;

    addToCart({
      id: product.id,
      name: product.name,
      price: parseFloat(product.price),
      image: product.images[0]?.src,
      validity_days: product.validity_days,
    });

    setAddedProducts((prev) => new Set(prev).add(product.id));

    setTimeout(() => {
      setAddedProducts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }, 500);
  };

  const [filteredProducts, setFilteredProducts] = useState<ShopProduct[]>([]);

  useEffect(() => {
    const filtered = products.filter((product) => {
      if (activeTab === "all") return true;

      const categoryId = parseInt(activeTab);
      return product.categories.some((category) => {
        return category.id === categoryId;
      });
    });
    setFilteredProducts(filtered);

    if (filtered.length > 0) {
      try {
        const ga4Items = filtered.map((product, index) =>
          mapShopProductToGA4Item(product, index),
        );
        trackGA4ViewItemList(ga4Items);
      } catch (error) {
        console.error("Error tracking GA4 view_item_list:", error);
      }
    }
  }, [products, activeTab]);

  return (
    <BaseLayout>
      <div className="min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-tenor-sans text-secondary mb-4">
              {t("Ogni esperienza è un viaggio.")}
            </h1>
            <p className="text-base text-gray-700 max-w-2xl mx-auto">
              {t(
                "Scopri le diverse formule di ingresso e vivi ChorusLife Immersive SPA secondo il tuo ritmo, tra relax, sapori e sensazioni che rigenerano.",
              )}
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-8">
            <div className="overflow-x-auto -mx-4 md:mx-0">
              <div className="flex items-center gap-2 justify-center md:justify-center min-w-max md:min-w-0 px-4 md:px-0">
                <button
                  onClick={() => handleTabChange("all")}
                  className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                    activeTab === "all"
                      ? "text-gray-900 font-bold"
                      : "text-gray-500"
                  }`}
                >
                  {t("Tutti")}
                </button>
                {categories.map((category, index) => (
                  <div key={category.id} className="flex items-center gap-2">
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => handleTabChange(category.id.toString())}
                      className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                        activeTab === category.id.toString()
                          ? "text-gray-900 font-bold"
                          : "text-gray-500"
                      }`}
                    >
                      {category.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-secondary border-t-transparent"></div>
              <p className="text-gray-500 text-lg mt-4">
                {t("Caricamento prodotti...")}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white overflow-hidden border-2 border-gray-200 p-2 flex flex-col"
                  >
                    <Link
                      to={getMenuHref(locale, `/shop/${product.slug}`)}
                      className="relative"
                    >
                      <div className="relative">
                        <img
                          src={
                            product.images[0]?.src || "/images/placeholder.png"
                          }
                          alt={product.images[0]?.alt || product.name}
                          className="w-full aspect-square object-cover"
                        />
                        <div className="absolute bottom-0 right-0 bg-secondary px-3 py-1">
                          <span className="text-white font-semibold text-sm">
                            €{" "}
                            {parseFloat(product.price)
                              .toFixed(2)
                              .replace(".", ",")}
                          </span>
                        </div>
                      </div>
                    </Link>
                    <div className="mt-4 flex flex-col flex-1">
                      <Link
                        to={getMenuHref(locale, `/shop/${product.slug}`)}
                        className="block mb-3"
                      >
                        <h3 className="text-lg font-medium text-gray-900 mb-3 min-h-16 hover:text-secondary transition-colors cursor-pointer">
                          {product.name}
                        </h3>
                      </Link>
                      {/* WooCommerce "short description", edited per product
                          (and per language) from the WP admin. Same role as
                          the subtitle on the booking cards. */}
                      {product.short_description?.replace(/<[^>]*>/g, "").trim() && (
                        <p className="text-sm text-gray-500 line-clamp-3 mb-3">
                          {product.short_description.replace(/<[^>]*>/g, "").trim()}
                        </p>
                      )}
                      <Button
                        onClick={() => handleAddToCart(product)}
                        className="w-full mt-auto bg-secondary text-white transition-all duration-300 relative overflow-hidden"
                        disabled={
                          product.stock_status === "outofstock" ||
                          addedProducts.has(product.id)
                        }
                      >
                        <AnimatePresence mode="wait">
                          {product.stock_status === "outofstock" ? (
                            <motion.span
                              key="unavailable"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              exit={{ y: -20, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {t("Non disponibile")}
                            </motion.span>
                          ) : addedProducts.has(product.id) ? (
                            <motion.span
                              key="added"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              exit={{ y: -20, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {t("Aggiunto al carrello")}
                            </motion.span>
                          ) : (
                            <motion.span
                              key="add"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              exit={{ y: -20, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {t("Aggiungi al carrello")}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredProducts.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    {t("Nessun prodotto trovato")}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </BaseLayout>
  );
}
